import { useToast } from '@act/react-components/app/ReusableToast';
import useConfirmEngagementDialog from '@act/react-components/dialog-forms/hooks/use-confirm-engagement-dialog';
import { RichTextEditorRef } from '@act/react-components/dialog-forms/rich-text-editor';
import { TrackingProvider } from '@act/react-components/tracking';
import { Permission } from '@act/shared/authorization';
import { CALL_INSUFFICENT_NOTES_OUTCOMES } from '@act/shared/constants';
import type {
  CallCreationRequestForm,
  CallDto,
  NeedDto,
  ScheduledEventDto,
  UpdateCallForm,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Button, FlexContainer, FormBuilder } from '@act/storybook';
import StyledAlert from '@act/toolkit/alerts/styled-alert';
import { useMobile } from '@act/utilities/mobile-context';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  FormState,
  Path,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  clientAppSelectors,
  selectMemberById,
  selectSelectedMember,
} from 'redux/redux';
import { autoSaveNotesRequests } from 'redux/requests/auto-save.requests';
import { callsRequests } from 'redux/requests/calls.request';
import {
  actingUserAppSelectors,
  selectActingUser,
} from 'redux/slices/actingUserSlice';
import { callsAppSelectors } from 'redux/slices/callsSlice';
import { selectFeedItemByFeedItemId } from 'redux/slices/feedSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import type { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { getEnvironment } from 'toolkit/environment/environment/environment';
import { DATE_TIME_FORMAT } from 'utilities/date-time-generator';

export interface PhoneCallDialogInterfaceProps {
  user: UserDto;
  call?: CallDto;
  scheduledEvent?: ScheduledEventDto;
  need?: NeedDto;
  isCustomResourcing?: boolean;
  fromResourceHub?: boolean;
  isFeedbackCall?: boolean;
  highlightedNumber?: string;
  completionCallback?: () => void;
  userNeeds?: { label: string; value: number }[];
}
interface PhoneCallDialogProps extends PhoneCallDialogInterfaceProps {
  handleClose: () => void;
  open: boolean;
  control: Control<UpdateCallForm | CallCreationRequestForm>;
  getValues: UseFormGetValues<UpdateCallForm | CallCreationRequestForm>;
  setValue: UseFormSetValue<UpdateCallForm | CallCreationRequestForm>;
  watch: UseFormWatch<UpdateCallForm | CallCreationRequestForm>;
  handleSubmit: UseFormHandleSubmit<UpdateCallForm | CallCreationRequestForm>;
  handleCancel: () => void;
  errors: any;
  formState: FormState<UpdateCallForm | CallCreationRequestForm>;
  onSaveFunctionReady?: (saveFn: () => Promise<void>) => void;
}

export const CallUpdateForm = ({
  handleSubmit,
  formState,
  control,
  getValues,
  setValue,
  watch,
  handleClose,
  call: existingCall,
  user,
  need,
  isCustomResourcing,
  userNeeds,
  completionCallback,
  onSaveFunctionReady,
}: PhoneCallDialogProps) => {
  const environment = getEnvironment();
  const { isMobile, orientation } = useMobile();
  const portrait = useMemo(() => orientation === 'portrait', [orientation]);
  const algorithmStartDate = DateTime.fromFormat(
    environment.memberStatusAlgorithmStartDate,
    DATE_TIME_FORMAT
  );
  const toast = useToast();

  const openConfirmEngagementDialog = useConfirmEngagementDialog();

  const [isEngageEnabledWhileActive, setEngagedEnabledWhileActive] =
    useState(false);
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const [newCall, _isCallLoading, _createCall] = useApiCall(
    callsRequests.createCall
  );

  const [updatedCall, _isUpdatingCall, updateCall] = useApiCall(
    callsRequests.updateCall
  );

  const [_deletedAutoSave, _isAutoSaveDeleting, deleteAutoSave] = useApiCall(
    autoSaveNotesRequests.deleteAutoSavedNote
  );

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isManualSave, setIsManualSave] = useState(false);
  const [pendingManualSave, setPendingManualSave] = useState<
    UpdateCallForm | CallCreationRequestForm | null
  >(null);

  const originalCallDataRef = useRef<Partial<CallDto> | null>(null);

  const call = newCall || existingCall;

  const canEditEngagement = call
    ? DateTime.fromMillis(call.timeStarted) > algorithmStartDate
    : true;

  const selectedOutcomeId = watch('outcomeId');

  const callFeedItemSelector = useMemo(
    () => selectFeedItemByFeedItemId(call?.id, 'call'),
    [call]
  );

  const actingUser = useAppSelector(selectActingUser);
  const callFeedItem = useAppSelector(callFeedItemSelector);
  const selectedCall = callsAppSelectors.useSelectCallById(
    callFeedItem?.externalItemId
  );

  const memberById = useAppSelector(selectMemberById(selectedCall?.subjectId));
  const selectedMemberFromStore = useAppSelector(selectSelectedMember);

  const selectedMember = selectedCall?.subjectId
    ? memberById
    : user || selectedMemberFromStore;

  const callEngagementLengthRequirement =
    clientAppSelectors.useSelectClientById(
      selectedMember?.clientId
    )?.callEngagementLengthRequirementInMinutes;

  const isResourcing =
    !!call?.resourceInteraction ||
    !!call?.needResourceInteraction ||
    isCustomResourcing;
  const latestCall = selectedCall || call;
  const targetOutcomeType =
    isResourcing || call?.needResourceInteraction
      ? 'resourcing'
      : 'interaction';

  const canOverrideUnderTwoMinuteEngagement =
    actingUserAppSelectors.useUserHasPermission(
      Permission.CAN_OVERRIDE_UNDER_TWO_MINUTE_ENGAGEMENT
    );

  const includeEngagedStatus: boolean = useMemo(() => {
    if (canOverrideUnderTwoMinuteEngagement) {
      return true;
    }
    if (latestCall?.timeConnected && latestCall?.timeEnded) {
      const ended = DateTime.fromMillis(latestCall?.timeEnded);
      const connected = DateTime.fromMillis(latestCall?.timeConnected);
      if (
        ended.diff(connected, 'minutes').minutes >=
        callEngagementLengthRequirement
      ) {
        return true;
      }
    }
    if (isEngageEnabledWhileActive) {
      return true;
    }
    return false;
  }, [actingUser, latestCall, isEngageEnabledWhileActive]);

  const outcomes = metaSliceSelectors.useSelectCallOutcomes();
  const outcomeOptions = metaSliceSelectors.useSelectCallOutcomeOptions(
    targetOutcomeType,
    includeEngagedStatus,
    call?.timeStarted
  );

  const isFromResourceHub = !!call?.resourceInteraction?.resource?.findHelpId;

  const isNeedBasedResourcing = isResourcing && need && !isFromResourceHub;

  useEffect(() => {
    let timer;

    if (latestCall?.timeConnected && !latestCall?.timeEnded) {
      const connectedAt = DateTime.fromMillis(latestCall?.timeConnected);
      const twoMinutesPassedAt = connectedAt.plus({ minutes: 2 });
      const difference = twoMinutesPassedAt.diff(
        DateTime.now(),
        'milliseconds'
      ).milliseconds;

      if (difference <= 0) {
        setEngagedEnabledWhileActive(true);
        return;
      }

      timer = setTimeout(() => {
        setEngagedEnabledWhileActive(true);
      }, difference);
    }

    return () => clearTimeout(timer);
  }, [latestCall]);

  const outcomeHasCharacterMinimum: boolean = useMemo(() => {
    if (!outcomeOptions || !selectedOutcomeId) {
      return false;
    }
    const outcome = outcomes.find(o => o.id === selectedOutcomeId);
    return CALL_INSUFFICENT_NOTES_OUTCOMES.includes(outcome?.key);
  }, [outcomeOptions, selectedOutcomeId]);

  useEffect(() => {
    if (call) {
      setValue('outcomeId', call.outcomeId);
      setValue('note', call?.note || '<p><br></p>');
      if (call.resourceInteraction) {
        setValue('resourceTitle', call.resourceInteraction.resource.title);
      }
      if (call.needResourceInteraction) {
        setValue('resourceTitle', call.needResourceInteraction.resource.title);
      }

      originalCallDataRef.current = {
        outcomeId: call.outcomeId,
        note: call.note,
        resourceInteraction: call.resourceInteraction,
        needResourceInteraction: call.needResourceInteraction,
      };
    }
  }, [call?.id]);

  const hasFormDataChanged = useCallback(
    (formData: UpdateCallForm): boolean => {
      if (!originalCallDataRef.current) {
        return true;
      }

      const original = originalCallDataRef.current;

      if (formData.outcomeId !== original.outcomeId) {
        return true;
      }

      if (formData.note !== original.note) {
        return true;
      }

      if (isResourcing) {
        const originalResourceName =
          original.resourceInteraction?.resource?.title ||
          original.needResourceInteraction?.resource?.title;
        if (formData.resourceTitle !== originalResourceName) {
          return true;
        }
        if (
          formData.needResourceInteraction?.userNeedId !==
          original.needResourceInteraction?.userNeedId
        ) {
          return true;
        }
      }

      return false;
    },
    [isResourcing]
  );

  useEffect(() => {
    if (!isSaving && updatedCall && !saveError && isManualSave) {
      if (completionCallback) {
        completionCallback();
      }
      toast('Call updated successfully');
      handleClose();
      setIsManualSave(false);
      setPendingManualSave(null);
    }
  }, [
    isSaving,
    updatedCall,
    saveError,
    isManualSave,
    completionCallback,
    handleClose,
  ]);

  const performSave = useCallback(
    async (data: UpdateCallForm, isManualSaveParam: boolean = false) => {
      if (isSaving) {
        return;
      }

      if (!call?.id) {
        return;
      }

      setIsSaving(true);
      setSaveError(null);

      if (isManualSaveParam) {
        setIsManualSave(true);
      }

      try {
        if (isManualSaveParam && richTextEditorRef.current) {
          richTextEditorRef.current.cancelPendingAutoSave();
        }

        const selectedOutcome = outcomes.find(
          outcome => outcome.id === data.outcomeId
        );

        if (
          isManualSaveParam &&
          user?.memberStage.key === 'outreach' &&
          selectedOutcome?.key === 'engaged'
        ) {
          const confirmed = await openConfirmEngagementDialog();
          if (!confirmed) {
            setIsSaving(false);
            setIsManualSave(false);
            return;
          }
        }

        const params = { id: call.id };
        const body: Partial<UpdateCallForm> = {
          note: data.note,
        };

        if (isManualSaveParam) {
          body.outcomeId = data.outcomeId;

          if (
            isResourcing &&
            (isNeedBasedResourcing || data?.needResourceInteraction?.userNeedId)
          ) {
            body.needResourceInteraction = {
              title: data?.resourceTitle,
              userNeedId: data?.needResourceInteraction?.userNeedId ?? need?.id,
            };
          }
        }

        updateCall({ params, body });

        if (originalCallDataRef.current) {
          originalCallDataRef.current = {
            ...originalCallDataRef.current,
            note: (data as UpdateCallForm).note,
            ...(isManualSaveParam && {
              outcomeId: (data as UpdateCallForm).outcomeId,
              ...(isResourcing && {
                resourceInteraction: isNeedBasedResourcing
                  ? undefined
                  : call?.resourceInteraction,
                needResourceInteraction: isNeedBasedResourcing
                  ? {
                      ...call?.needResourceInteraction,
                      resource: {
                        ...call?.needResourceInteraction?.resource,
                        title: data.resourceTitle,
                      },
                    }
                  : undefined,
              }),
            }),
          };
        }

        if (isManualSaveParam) {
          deleteAutoSave({
            params: {
              subjectId: selectedMember?.id || user?.id,
              type: 'calls',
            },
          });

          if (richTextEditorRef.current) {
            richTextEditorRef.current.markAsSaved(data.note);
          }
        }
      } catch (err) {
        console.error(
          `Error ${isManualSaveParam ? 'saving' : 'auto-saving'} call:`,
          err
        );
        setSaveError(err instanceof Error ? err.message : 'Save failed');
        setIsManualSave(false);
      } finally {
        setIsSaving(false);
      }
    },
    [
      isSaving,
      getValues,
      outcomes,
      user,
      openConfirmEngagementDialog,
      call,
      isResourcing,
      isNeedBasedResourcing,
      isFromResourceHub,
      need,
      updateCall,
      deleteAutoSave,
    ]
  );

  useEffect(() => {
    if (!isSaving && pendingManualSave && !isManualSave) {
      void performSave(pendingManualSave, true);
    }
  }, [isSaving, pendingManualSave, isManualSave, performSave]);

  const handleSave = useCallback(
    async (data?: UpdateCallForm) => {
      const allFormValues = getValues();
      const formData = data ? { ...allFormValues, ...data } : allFormValues;
      if (richTextEditorRef.current) {
        const currentNoteValue = richTextEditorRef.current.getCurrentValue();
        (formData as UpdateCallForm).note = currentNoteValue;
      }

      if (!hasFormDataChanged(formData as UpdateCallForm)) {
        handleClose();
        return;
      }

      if (isSaving) {
        setPendingManualSave(formData as UpdateCallForm);
        return;
      }

      await performSave(formData, true);
    },
    [getValues, performSave, isSaving, hasFormDataChanged, handleClose]
  );

  // Expose save function to parent
  useEffect(() => {
    if (onSaveFunctionReady) {
      onSaveFunctionReady(async () => {
        await handleSave();
      });
    }
  }, [onSaveFunctionReady, handleSave]);

  function handleAutoSave(autoSaveNote: string) {
    if (!call?.id) {
      return;
    }

    if (isSaving || pendingManualSave) {
      return;
    }

    const originalNote = originalCallDataRef.current?.note || '';
    if (autoSaveNote === originalNote) {
      return;
    }

    const autoSaveData: UpdateCallForm = {
      ...getValues(),
      note: autoSaveNote,
    };

    void performSave(autoSaveData, false);
  }

  const formConfig = useMemo(() => {
    const config: FormConfig<UpdateCallForm> = [];

    config.push({
      name: 'outcomeId' as const,
      label: 'Outcome',
      input: 'select' as const,
      type: 'input' as const,
      trackingName: 'outcome-select',
      disabled: !canEditEngagement,
      props: {
        options: outcomeOptions,
      },
    });

    if (!isNeedBasedResourcing && outcomeHasCharacterMinimum) {
      config.push({
        type: 'passthrough' as const,
        content: (
          <StyledAlert
            variant="tonal"
            severity="neutralDark"
            message="For this outcome a note of 50 characters or more is required."
          />
        ),
      });
    }

    if (isNeedBasedResourcing || !!call?.resourceInteraction?.interactionId) {
      config.push({
        name: 'needResourceInteraction.userNeedId' as Path<UpdateCallForm>,
        label: 'User Need',
        input: 'select' as const,
        type: 'input' as const,
        trackingName: 'user-need-select',
        props: {
          options: userNeeds,
        },
      });
    }

    config.push({
      name: 'note' as const,
      label: 'Note',
      input: 'richText' as const,
      type: 'input' as const,
      trackingName: 'note-input',
      props: {
        autoSaveFn: handleAutoSave,
        showCharacterCount: true,
        height: isMobile && portrait ? '400px' : 350,
        defaultValue: call?.note || '<p><br></p>',
        style: {
          height: '100%',
        },
      },
    });

    return config;
  }, [
    outcomeOptions,
    canEditEngagement,
    outcomeHasCharacterMinimum,
    call?.resourceInteraction?.interactionId,
  ]);

  return (
    <TrackingProvider contextName="call-form">
      <form
        id="call-form"
        onSubmit={handleSubmit(handleSave)}
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          marginTop: isMobile ? 0 : '64px',
        }}
      >
        <FlexContainer
          direction="column"
          gap={2}
          height="auto"
          padding={isMobile ? 1 : 3}
          width={'100%'}
          sx={{
            boxSizing: 'border-box',
            overflow: 'visible',
          }}
        >
          <Typography variant="titleLarge" color="text.primary">
            My Notes
          </Typography>
          {!call?.isFeedbackCall && (
            <FormBuilder
              config={formConfig as FormConfig<UpdateCallForm>}
              control={control}
              formState={formState}
            />
          )}
        </FlexContainer>
        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignSelf: 'flex-end',
              padding: '16px',
            }}
          >
            <Button variant="outlined" type="submit">
              Save & Close
            </Button>
          </Box>
        )}
      </form>
    </TrackingProvider>
  );
};
